import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ActivitiesGateway } from '@wilson/api/gateway';
import {
  AccurateActivityTimeDetails,
  AccurateDateIntervalWithShift,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  Shift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import {
  DropZoneEventThrottlerDirective,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  RemoveActivityFromUnassignedRegion,
  SetDemarcatorRange,
  ShiftTimelineDataState,
  TimelineRemoveActivityFromShiftV2,
  TimelineStaysSuggestionService,
  TimelineUpdateShiftV2,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { COMMON_SHIFT_RELATIONS, ShiftsService } from '@wilson/shifts';
import { isAfter, isBefore } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, firstValueFrom } from 'rxjs';
import { ActivityDropZoneHelperService } from '../../components/activity-drop-zone/activity-drop-zone-helper.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { activitiesCompareFn } from '@wilson/activities/util';
import { faBan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'wilson-activity-shift-drop-zone-v2',
  standalone: true,
  imports: [
    CommonModule,
    ShiftTimelinePipesModule,
    GanttEndingPixelPipe,
    FontAwesomeModule,
  ],
  template: `
    @if (
      {
        left: leftPosition$ | async,
        right:
          ([accurateStartDateTimeDetails.date, accurateEndDateTimeDetails.date]
          | ganttEndingPixel
          | async),
      };
      as positionData
    ) {
      @if (!isDropAllowed && isHovered) {
        <div
          [style.left.px]="positionData.left"
          [style.right.px]="positionData.right"
          class="activityDropZone not-allowed"
        >
          <fa-icon [icon]="faBan" class="icon"></fa-icon>
        </div>
      } @else {
        <div
          [style.left.px]="positionData.left"
          [style.right.px]="positionData.right"
          class="activityDropZone"
        ></div>
      }
    }
  `,
  styleUrls: ['./activity-shift-drop-zone-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
  ],
})
export class ActivityShiftDropZoneV2Component
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly activitiesService = inject(ActivitiesGateway);
  private readonly shiftsService = inject(ShiftsService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly ganttShiftStartTimePipe = inject(GanttShiftStartTimePipe);
  private readonly ganttShiftEndTimePipe = inject(GanttShiftEndTimePipe);
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  @Input() userId!: string | undefined;
  @Input() shiftView!: AccurateDateIntervalWithShift;
  @Input() isDropAllowed!: boolean | null;

  public isHovered = false;

  leftPosition$!: Observable<number>;
  accurateStartDateTimeDetails!: AccurateActivityTimeDetails;
  accurateEndDateTimeDetails!: AccurateActivityTimeDetails;
  activities!: ActivityWithCategoryAndLocationsAndReportedDetails[];
  previousUserId!: string | null;

  readonly faBan = faBan;

  async ngOnInit(): Promise<void> {
    this.activities = this.store.selectSnapshot(
      ShiftTimelineDataState.shiftActivitiesArray(this.shiftView.shift.id),
    );

    this.accurateStartDateTimeDetails = this.ganttShiftStartTimePipe.transform(
      this.activities,
    );

    this.accurateEndDateTimeDetails = this.ganttShiftEndTimePipe.transform(
      this.activities,
    );

    this.leftPosition$ = this.ganttActivityStartingPixelPipe.transform(
      this.accurateStartDateTimeDetails.date,
    );
  }

  protected async onDragOverHandler(): Promise<void> {
    this.isHovered = true;
    if (this.isDropAllowed) {
      this.previousUserId = await firstValueFrom(
        this.activityDropZoneHelperService.userId$,
      );
      const shiftToUpdateId = this.shiftView.shift.id;
      const activity = await firstValueFrom(
        this.activityDropZoneHelperService.activity$,
      );
      if (activity && this.userId && shiftToUpdateId !== activity.shiftId) {
        if (
          isBefore(
            new Date(activity.startDatetime),
            this.shiftView.accurateStartDateTime,
          )
        ) {
          this.store.dispatch(
            new TimelineUpdateShiftV2({
              ...this.shiftView.shift,
              startDatetime: activity.startDatetime,
              startLocation: {
                locationCode: activity.startLocation?.locationCode as string,
                name: activity.startLocation?.name as string,
              },
              startLocationId: activity.startLocationId as string,
            }),
          );
        }
        if (
          isAfter(
            new Date(activity.endDatetime),
            this.shiftView.accurateEndDateTime,
          )
        ) {
          this.store.dispatch(
            new TimelineUpdateShiftV2({
              ...this.shiftView.shift,
              endDatetime: activity.endDatetime,
              endLocation: {
                locationCode: activity.endLocation?.locationCode as string,
                name: activity.endLocation?.name as string,
              },
              endLocationId: activity.endLocationId as string,
            }),
          );
        }
        this.store.dispatch(
          new TimelineUpsertActivityV2([activity], shiftToUpdateId),
        );
      }
    }
  }

  protected async onDragLeaveHandler(): Promise<void> {
    this.isHovered = false;
    const shiftToUpdateId = this.shiftView.shift.id;
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    if (activity && this.userId && shiftToUpdateId !== activity.shiftId) {
      this.store.dispatch(
        new TimelineRemoveActivityFromShiftV2(
          activity.id as string,
          shiftToUpdateId,
        ),
      );
      const activities = this.store.selectSnapshot(
        ShiftTimelineDataState.shiftActivitiesArray(shiftToUpdateId),
      );
      activities.sort(activitiesCompareFn);
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...this.shiftView.shift,
          startDatetime: activities[0].startDatetime,
          startLocation: {
            locationCode: activities[0].startLocation.locationCode || '',
            name: activities[0].startLocation.name,
          },
          startLocationId: activities[0].startLocationId || '',
          endDatetime: activities[activities.length - 1].endDatetime,
          endLocation: {
            locationCode:
              activities[activities.length - 1].endLocation.locationCode || '',
            name: activities[activities.length - 1].endLocation.name,
          },
          endLocationId: activities[activities.length - 1].endLocationId || '',
        }),
      );
    }
  }

  protected async onDropHandler(): Promise<void> {
    this.isHovered = false;
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    const shiftToUpdateId = this.shiftView.shift.id;
    if (this.isDropAllowed && shiftToUpdateId !== activity?.shiftId) {
      const isAssignmentMode = await firstValueFrom(
        this.dragAndDropModeService.isAssignmentMode$,
      );

      await this.handleAssignActivity(activity);
      if (isAssignmentMode && activity?.shiftId) {
        await this.activityDropZoneHelperService.handleAssignActivityToExistingShift(
          activity,
          activity.shiftId,
        );
      }
      this.store.dispatch(new SetDemarcatorRange([]));
    }
  }

  private async handleAssignActivity(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails | null,
  ): Promise<void> {
    const shiftToUpdateId = this.shiftView.shift.id;
    if (this.userId && activity?.id) {
      if (shiftToUpdateId) {
        try {
          this.createNewViaDragAndDropService.addUpdatingId(shiftToUpdateId);
          await this.activitiesService.updateActivity({
            id: activity.id,
            shiftId: shiftToUpdateId,
          });
          const updatedShift = await firstValueFrom(
            this.shiftsService.get<
              Shift &
                WithPreparedAttributes & {
                  id: string;
                }
            >(shiftToUpdateId, {
              relations: COMMON_SHIFT_RELATIONS,
            }),
          );
          this.store.dispatch([
            new TimelineUpdateShiftV2({
              ...updatedShift,
              isHighlighted: true,
            }),
            new TimelineUpsertActivityV2(
              [{ ...activity, shiftId: shiftToUpdateId }],
              shiftToUpdateId,
            ),

            new RemoveActivityFromUnassignedRegion(activity),
          ]);

          this.timelineCommonOperationsService.updateUsersAssignmentsState(
            this.previousUserId
              ? [this.userId, this.previousUserId]
              : [this.userId],
          );
          this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
            this.previousUserId
              ? [this.userId, this.previousUserId]
              : [this.userId],
          );
          this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
            this.previousUserId
              ? [this.userId, this.previousUserId]
              : [this.userId],
          );
          this.createNewViaDragAndDropService.removeUpdatingId(shiftToUpdateId);
          this.activityDropZoneHelperService.setActivity(null);
          this.activityDropZoneHelperService.setUserId(null);
        } catch {
          this.store.dispatch([
            new TimelineUpdateShiftV2({
              ...this.shiftView.shift,
              startDatetime: this.accurateStartDateTimeDetails.date,
              startLocation: {
                locationCode: this.accurateStartDateTimeDetails.location
                  ?.locationCode as string,
                name: this.accurateStartDateTimeDetails.location
                  ?.name as string,
              },
              startLocationId: this.accurateStartDateTimeDetails.location
                ?.id as string,
            }),
            new TimelineRemoveActivityFromShiftV2(activity.id, shiftToUpdateId),
          ]);
          this.createNewViaDragAndDropService.removeUpdatingId(shiftToUpdateId);
          this.nzMessageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
        }
      }
    }
  }
}
